import { Field } from "@/type/common";
import { Label } from "@/components/ui/label";
import SelectWithSearch from "@/components/ui/select-search";
import SimpleSelect from "@/components/ui/simple-select";
import { useGraphConfig } from "@/store/contexts/GraphConfigContext";
import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useContext, useMemo } from "react";
import GraphConfigFormContext from "@/store/contexts/GraphConfigFormContext";

type GroupConfigProps = {};

const GroupConfig = ({}: GroupConfigProps = {}) => {
  const { fields } = useGraphConfig();
  const { config, setConfig } = useContext(GraphConfigFormContext);

  if (!config) return null;

  const updateDataSource = (updates: any) => {
    setConfig({
      ...config,
      data_source: {
        ...config.data_source,
        ...updates,
      },
    });
  };

  // Initialize groupBy array if it's not already
  if (!Array.isArray(config.data_source.groupBy)) {
    updateDataSource({
      groupBy: [config.data_source.groupBy],
    });
    return null;
  }

  const filteredFields = fields.filter(
    (field: Field) => !["binary"].includes(field.type)
  ) as any;

  const updateGroupBy = (index: number, updates: any) => {
    const newGroupBy = [...config.data_source.groupBy];
    newGroupBy[index] = {
      ...newGroupBy[index],
      ...updates,
    };
    updateDataSource({ groupBy: newGroupBy });
  };

  const addGroupBy = () => {
    if (config.data_source.groupBy.length >= 2) return;

    const newGroupBy = [
      ...config.data_source.groupBy,
      { field: "", interval: "auto", show_empty: false },
    ];
    updateDataSource({ groupBy: newGroupBy });
  };

  const removeGroupBy = (index: number) => {
    const newGroupBy = [...config.data_source.groupBy];
    newGroupBy.splice(index, 1);
    updateDataSource({ groupBy: newGroupBy });
  };

  const updateOrderBy = (updates: any) => {
    updateDataSource({
      orderBy: {
        ...config.data_source.orderBy,
        ...updates,
      },
    });
  };

  const allFields = useMemo(() => {
    const allFields = [];
    if (config.type === "graph" && config.graph_options?.measures?.length > 0) {
      allFields.push(...config.graph_options.measures.map((m: any) => m.field));
    }
    if (config.type === "table" && config.table_options?.columns?.length > 0) {
      allFields.push(...config.table_options.columns.map((c: any) => c.field));
    }
    if (config.data_source?.groupBy?.length > 0) {
      allFields.push(...config.data_source.groupBy.map((g: any) => g.field));
    }
    return allFields;
  }, [config]);

  return (
    <div className="flex flex-col gap-4">
      {config.data_source.groupBy.map((groupBy: any, index: number) => {
        const groupByField = fields.find(
          (field: Field) => field.field === groupBy.field
        );

        return (
          <div
            key={index}
            className="flex flex-col gap-2 border rounded-md p-3"
          >
            <div className="flex flex-row items-center justify-between w-full">
              <Label>Group by {index + 1}</Label>
              <div className="flex items-center gap-2">
                <div className="w-60">
                  <SelectWithSearch
                    value={groupBy.field}
                    options={filteredFields}
                    placeholder="Select field..."
                    onChange={(value) => updateGroupBy(index, { field: value })}
                  />
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeGroupBy(index)}
                  className="text-red-500"
                >
                  <Trash2 size={18} />
                </Button>
              </div>
            </div>
            {groupByField &&
              ["date", "datetime"].includes(groupByField.type) && (
                <div className="flex flex-row items-center justify-between w-full">
                  <Label>Date aggregation</Label>
                  <div className="w-60">
                    <SimpleSelect
                      value={groupBy.interval || "auto"}
                      options={[
                        { value: "auto", label: "Auto" },
                        { value: "day", label: "Day" },
                        { value: "week", label: "Week" },
                        { value: "month", label: "Month" },
                        { value: "quarter", label: "Quarter" },
                        { value: "year", label: "Year" },
                      ]}
                      onChange={(value) =>
                        updateGroupBy(index, { interval: value as any })
                      }
                    />
                  </div>
                </div>
              )}
            <div className="flex flex-row items-center justify-between w-full">
              <Label>Show empty values</Label>
              <Switch
                checked={groupBy.show_empty}
                onCheckedChange={(value) =>
                  updateGroupBy(index, { show_empty: value })
                }
              />
            </div>
          </div>
        );
      })}

      {config.data_source.groupBy.length < 2 &&
        ((config.type !== "table" &&
          !["pie", "donut"].includes(config.graph_options.chartType)) ||
          config.data_source.groupBy.length < 1) && (
          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={addGroupBy}
              className="flex items-center gap-1"
            >
              <Plus size={16} />
              Add Group By
            </Button>
          </div>
        )}
      <div className="flex flex-row items-center justify-between w-full mt-2">
        <Label>Sort by</Label>
        <div className="flex flex-row items-center gap-2 w-3/5">
          <div className="w-full">
            <SelectWithSearch
              value={config.data_source.orderBy.field}
              options={
                fields.filter((field: Field) =>
                  allFields.includes(field.field)
                ) as any
              }
              placeholder="Select field..."
              onChange={(value) => updateOrderBy({ field: value })}
            />
          </div>
          <div>
            <SimpleSelect
              value={config.data_source.orderBy.direction}
              options={[
                { value: "asc", label: "Ascending" },
                { value: "desc", label: "Descending" },
              ]}
              onChange={(value) => updateOrderBy({ direction: value as any })}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupConfig;
